import React from "react";
import MarketingLayout from "../../components/layout/marketing/MarketingLayout";

export default function Terms() {
    return (
        <MarketingLayout>
            <div className="mx-auto px-8 lg:px-24 py-20 sm:py-24 lg:py-16">
                {/* Header */}
                <div className="border-b border-border-subtle pb-8">
                    <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-text-primary">
                        Terms of <span className="bg-brand-gradient bg-clip-text text-transparent">Service</span>
                    </h1>
                    <p className="mt-4 text-lg text-text-muted">Last Updated: January 2026</p>
                </div>

                <div className="mt-12 space-y-10">
                    {/* 1. Acceptance */}
                    <article>
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            <span className="h-8 w-1 bg-primary rounded-full" />
                            1. Acceptance of Terms
                        </h2>
                        <p className="mt-4 text-text-secondary leading-relaxed">
                            By creating an account, subscribing to, or using Eikos Health ("Service," "we," "us"), you
                            ("User," "Clinic," or "Practitioner") agree to be bound by these Terms of Service. If you do
                            not agree, you must strictly avoid using our services.
                        </p>
                    </article>

                    {/* 2. Service Description */}
                    <article>
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            <span className="h-8 w-1 bg-primary rounded-full" />
                            2. Service Description
                        </h2>
                        <p className="mt-4 text-text-secondary leading-relaxed">
                            Eikos Health provides a scheduling and practice management tool that integrates with
                            WhatsApp. Our Service automates patient appointment booking, reminders, and schedule
                            management via third-party messaging platforms.
                        </p>
                    </article>

                    {/* 3. Platform Dependency */}
                    <article className="bg-bg-subtle/50 p-6 rounded-2xl border border-border-subtle">
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            <span className="h-8 w-1 bg-primary rounded-full" />
                            3. Platform Dependency (WhatsApp)
                        </h2>
                        <div className="mt-4 space-y-4 text-text-secondary leading-relaxed">
                            <p>
                                This is a critical condition of our service. You acknowledge that Eikos Health operates
                                via the WhatsApp Business API. Eikos Health is an independent service and is not
                                affiliated with, endorsed by, or sponsored by Meta Platforms, Inc. (WhatsApp).
                            </p>
                            <p>
                                <strong>Service Continuity:</strong> We are not responsible for service interruptions,
                                outages, or data delays caused by WhatsApp or Meta.
                            </p>
                            <p>
                                <strong>Account Bans:</strong> You agree to strictly adhere to WhatsApp’s Commerce and
                                Business Policies. If your phone number/account is banned by WhatsApp due to policy
                                violations (e.g., spamming patients, sending prohibited content), Eikos Health is not
                                liable to refund subscription fees.
                            </p>
                        </div>
                    </article>

                    {/* 4. User Responsibilities */}
                    <article>
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            <span className="h-8 w-1 bg-primary rounded-full" />
                            4. User Responsibilities & Consent
                        </h2>
                        <div className="mt-4 space-y-4 text-text-secondary leading-relaxed">
                            <p>
                                <strong>Patient Consent (Opt-In):</strong> You represent and warrant that you have
                                obtained explicit consent from your patients to contact them via WhatsApp regarding
                                their appointments, in compliance with local regulations (e.g., DPDP Act) and WhatsApp’s
                                policies.
                            </p>
                            <p>
                                <strong>Single Practitioner License:</strong> Your subscription grants a license for use
                                by a Single Named Practitioner associated with one designated WhatsApp phone number.
                                This license is non-transferable and may not be shared among multiple doctors, even if
                                they operate within the same clinic or facility.
                            </p>
                            <p>
                                <strong>No Resale:</strong> You may not resell, lease, or sub-license the Service to
                                other practitioners or third parties.
                            </p>
                            <p>
                                <strong>Account Security:</strong> You are responsible for maintaining the
                                confidentiality of your login credentials.
                            </p>
                        </div>
                    </article>

                    {/* 5. Medical Disclaimer */}
                    <article className="border-l-4 border-primary pl-6 py-2">
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            5. Medical Disclaimer
                        </h2>
                        <p className="mt-4 text-text-secondary leading-relaxed">
                            Eikos Health is a technology provider, not a healthcare provider. We do not provide medical
                            advice, diagnoses, or treatment. You acknowledge that you act as the sole medical
                            professional and assume full responsibility for all medical services provided to your
                            patients. Eikos Health shall not be liable for any malpractice, misdiagnosis, or patient
                            injury.
                        </p>
                    </article>

                    {/* 6. Data Privacy */}
                    <article>
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            <span className="h-8 w-1 bg-primary rounded-full" />
                            6. Data Privacy & Ownership
                        </h2>
                        <div className="mt-4 space-y-4 text-text-secondary leading-relaxed">
                            <p>
                                <strong>Patient Data:</strong> You retain full ownership of your patient records and
                                Personal Health Information (PHI) entered into the system. Eikos Health acts as a "Data
                                Processor" on your behalf.
                            </p>
                            <p>
                                <strong>Confidentiality:</strong> We employ industry-standard encryption to protect
                                data. However, you acknowledge that data transmission over third-party networks
                                (WhatsApp) is subject to those platforms' security protocols.
                            </p>
                        </div>
                    </article>

                    {/* 7. Limitation of Liability */}
                    <article>
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            <span className="h-8 w-1 bg-primary rounded-full" />
                            7. Limitation of Liability
                        </h2>
                        <div className="mt-4 space-y-4 text-text-secondary leading-relaxed">
                            <p>
                                <strong>Cap on Liability:</strong> Eikos Health’s total liability for any claims arising
                                out of this agreement shall be limited to the amount you paid us in the three (3) months
                                preceding the incident.
                            </p>
                            <p>
                                <strong>Exclusion of Damages:</strong> We are not liable for any indirect, incidental,
                                or consequential damages, including but not limited to loss of business revenue, loss of
                                patient data, or reputational harm.
                            </p>
                        </div>
                    </article>

                    {/* 8. Termination */}
                    <article>
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            <span className="h-8 w-1 bg-primary rounded-full" />
                            8. Termination
                        </h2>
                        <p className="mt-4 text-text-secondary leading-relaxed">
                            We reserve the right to suspend or terminate your account immediately if you violate these
                            Terms, fail to pay subscription fees, or violate WhatsApp’s Business Policies, resulting in
                            risk to our API integrity. Upon termination, you will be granted a 30-day window to export
                            your patient data, after which your data may be permanently deleted from our servers.
                        </p>
                    </article>

                    {/* 9. Governing Law */}
                    <article className="pt-8 border-t border-border-subtle">
                        <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                            <span className="h-8 w-1 bg-primary rounded-full" />
                            9. Governing Law
                        </h2>
                        <p className="mt-4 text-text-secondary leading-relaxed">
                            These Terms shall be governed by and construed in accordance with the laws of India. Any
                            disputes arising under these Terms shall be subject to the exclusive jurisdiction of the
                            courts located in Bengaluru.
                        </p>
                    </article>
                </div>
            </div>
        </MarketingLayout>
    );
}
